/**
 * StressTestSummary - Adversarial Analysis Results Card
 *
 * Shows a compact summary of adjuster challenge analysis:
 * - Total challenges identified
 * - Auto-resolved vs requiring judgment
 * - Overall claim resilience
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface StressTestData {
  supported_total: number;
  questioned_total: number;
  denied_total: number;
  attorney_comparison: {
    attorney_maximum: number;
    variance_percentage: number;
  };
  top_reduction_factors: string[];
  confidence_level: string;
}

interface StressTestSummaryProps {
  reportId: string;
  className?: string;
}

export default function StressTestSummary({ reportId, className = '' }: StressTestSummaryProps) {
  const { getToken } = useAuth();
  const [data, setData] = useState<StressTestData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (reportId) fetchAnalysis();
  }, [reportId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const result = await api.get(`/api/v1/adjuster/analysis/${reportId}/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setData(result);
    } catch (err) {
      console.debug('Adjuster analysis not available');
      setData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center mb-2">
          <Shield className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-sm font-semibold text-gray-900">Adversarial Analysis</h3>
        </div>
        <p className="text-xs text-gray-500">No analysis available yet</p>
        {error && (
          <button onClick={fetchAnalysis} className="mt-1 text-xs text-blue-600 hover:text-blue-700">
            Retry
          </button>
        )}
      </div>
    );
  }

  const totalChallenges = data.top_reduction_factors.length;
  const autoResolved = data.supported_total > 0 ? Math.min(totalChallenges, Math.ceil(totalChallenges * 0.6)) : 0;
  const needsJudgment = totalChallenges - autoResolved;
  const isStrong = data.confidence_level === 'HIGH' && data.denied_total === 0;
  const isWeak = data.confidence_level === 'LOW' || data.denied_total > data.supported_total;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className={`w-5 h-5 mr-2 ${isStrong ? 'text-green-600' : isWeak ? 'text-red-600' : 'text-blue-600'}`} />
            <h3 className="text-sm font-semibold text-gray-900">Adversarial Analysis</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              isStrong ? 'bg-green-100 text-green-700' :
              isWeak ? 'bg-red-100 text-red-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {isStrong ? 'Strong' : isWeak ? 'Weak' : 'Moderate'}
            </span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Summary metrics */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-lg font-bold text-gray-900">{totalChallenges}</div>
            <div className="text-[10px] text-gray-500">Challenges</div>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <div className="text-lg font-bold text-green-700">{autoResolved}</div>
            <div className="text-[10px] text-green-600">Resolved</div>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded-lg">
            <div className="text-lg font-bold text-amber-700">{needsJudgment}</div>
            <div className="text-[10px] text-amber-600">Need Review</div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Claim amounts */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Supported</span>
              <span className="font-medium text-green-700">
                ${data.supported_total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Questioned</span>
              <span className="font-medium text-amber-700">
                ${data.questioned_total.toLocaleString()}
              </span>
            </div>
            {data.denied_total > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Denied</span>
                <span className="font-medium text-red-700">
                  ${data.denied_total.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Top reduction factors */}
          {data.top_reduction_factors.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-700 mb-2">Key Challenges</h4>
              <div className="space-y-1.5">
                {data.top_reduction_factors.map((factor, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    {i < autoResolved ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-gray-600">{factor}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom line */}
          <p className={`text-xs font-medium ${isStrong ? 'text-green-700' : isWeak ? 'text-red-700' : 'text-amber-700'}`}>
            {isStrong
              ? 'Claim held up under conservative parameters'
              : isWeak
              ? 'Claim has significant vulnerabilities requiring attention'
              : 'Claim needs review on questioned items'}
          </p>
        </div>
      )}
    </div>
  );
}
